import React, { useState, useEffect } from 'react';
import { FaUser, FaUserFriends, FaDog, FaTree, FaPlus, FaEdit, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';

const CharacterManager = ({ storyId, onCharacterUpdate, initialCharacters = [] }) => {
  const [characters, setCharacters] = useState(initialCharacters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [newCharacter, setNewCharacter] = useState({
    name: '',
    type: 'human',
    age: '',
    gender: '',
    description: '',
    hairColor: '',
    hairStyle: '',
    eyeColor: '',
    skinTone: '',
    height: '',
    build: '',
    distinctiveFeatures: [],
    clothing: {
      top: '',
      bottom: '',
      accessories: [],
      colors: [],
      style: ''
    },
    personality: '',
    role: ''
  });

  // Load characters when component mounts
  useEffect(() => {
    if (storyId) {
      loadCharacters();
    }
  }, [storyId]);

  const loadCharacters = async () => {
    if (!storyId) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/story/${storyId}`);
      if (response.ok) {
        const data = await response.json();
        setCharacters(data.characters || []);
        if (onCharacterUpdate) {
          onCharacterUpdate(data.characters || []);
        }
      }
    } catch (error) {
      console.error('Error loading characters:', error);
      setError('Failed to load characters');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCharacter = async (e) => {
    e.preventDefault();
    if (!newCharacter.name.trim()) {
      setError('Character name is required');
      return;
    }

    setLoading(true);
    try {
      const characterData = {
        name: newCharacter.name.trim(),
        type: newCharacter.type,
        age: newCharacter.age || null,
        gender: newCharacter.gender || null,
        description: newCharacter.description.trim(),
        hairColor: newCharacter.hairColor || 'unknown',
        hairStyle: newCharacter.hairStyle || 'unknown',
        eyeColor: newCharacter.eyeColor || 'unknown',
        skinTone: newCharacter.skinTone || 'unknown',
        height: newCharacter.height || 'unknown',
        build: newCharacter.build || 'unknown',
        distinctiveFeatures: newCharacter.distinctiveFeatures || [],
        clothing: newCharacter.clothing,
        personality: newCharacter.personality || '',
        role: newCharacter.role || ''
      };

      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storyId,
          characterData
        })
      });

      if (!response.ok) throw new Error('Failed to create character');

      const data = await response.json();
      const updatedCharacters = [...characters, data.character];
      setCharacters(updatedCharacters);
      if (onCharacterUpdate) {
        onCharacterUpdate(updatedCharacters);
      }
      
      // Reset form
      setNewCharacter({
        name: '',
        type: 'human',
        age: '',
        gender: '',
        description: '',
        hairColor: '',
        hairStyle: '',
        eyeColor: '',
        skinTone: '',
        height: '',
        build: '',
        distinctiveFeatures: [],
        clothing: {
          top: '',
          bottom: '',
          accessories: [],
          colors: [],
          style: ''
        },
        personality: '',
        role: ''
      });
      setShowAddForm(false);
      setError(null);
    } catch (error) {
      console.error('Error creating character:', error);
      setError('Failed to create character');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCharacter = async (characterId, updateData) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/${characterId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) throw new Error('Failed to update character');

      const updatedCharacters = characters.map(char => 
        char.characterId === characterId ? { ...char, ...updateData } : char
      );
      setCharacters(updatedCharacters);
      if (onCharacterUpdate) {
        onCharacterUpdate(updatedCharacters);
      }
      setEditingCharacter(null);
      setError(null);
    } catch (error) {
      console.error('Error updating character:', error);
      setError('Failed to update character');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCharacter = async (characterId) => {
    if (!window.confirm('Are you sure you want to delete this character?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/${characterId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete character');

      const updatedCharacters = characters.filter(char => char.characterId !== characterId);
      setCharacters(updatedCharacters);
      if (onCharacterUpdate) {
        onCharacterUpdate(updatedCharacters);
      }
      setError(null);
    } catch (error) {
      console.error('Error deleting character:', error);
      setError('Failed to delete character');
    } finally {
      setLoading(false);
    }
  };

  const getCharacterIcon = (type) => {
    switch (type) {
      case 'human':
        return <FaUser className="text-blue-500" />;
      case 'animal':
        return <FaDog className="text-orange-500" />;
      case 'object':
        return <FaTree className="text-green-500" />;
      default:
        return <FaUserFriends className="text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-amber-800 flex items-center gap-2">
          <FaUserFriends />
          Character Manager
        </h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-gradient-to-r from-amber-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:shadow-md transition-all duration-300 flex items-center gap-2"
        >
          <FaPlus />
          Add Character
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Add Character Form */}
      {showAddForm && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-amber-800 mb-4">Add New Character</h3>
          <form onSubmit={handleCreateCharacter} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={newCharacter.name}
                onChange={(e) => setNewCharacter(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Character name"
                className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                required
              />
              <select
                value={newCharacter.type}
                onChange={(e) => setNewCharacter(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
              >
                <option value="human">Human</option>
                <option value="animal">Animal</option>
                <option value="object">Object</option>
              </select>
              <input
                type="text"
                value={newCharacter.age}
                onChange={(e) => setNewCharacter(prev => ({ ...prev, age: e.target.value }))}
                placeholder="Age (optional)"
                className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
              />
              <select
                value={newCharacter.gender}
                onChange={(e) => setNewCharacter(prev => ({ ...prev, gender: e.target.value }))}
                className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
              >
                <option value="">Gender (optional)</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
              </select>
            </div>
            <textarea
              value={newCharacter.description}
              onChange={(e) => setNewCharacter(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Character description"
              className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
              rows="3"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-amber-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:shadow-md transition-all duration-300 disabled:opacity-50 flex items-center gap-2"
              >
                <FaSave />
                {loading ? 'Creating...' : 'Create Character'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50 transition-all duration-300 flex items-center gap-2"
              >
                <FaTimes />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Characters List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-amber-800">Your Characters ({characters.length})</h3>
        
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            <span className="ml-2 text-amber-600">Loading characters...</span>
          </div>
        )}

        {characters.length === 0 && !loading ? (
          <div className="text-center py-8 text-amber-600">
            <FaUserFriends className="text-4xl mx-auto mb-4 opacity-50" />
            <p>No characters created yet. Add your first character to get started!</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {characters.map((character) => (
              <div
                key={character.characterId}
                className="bg-white border border-amber-200 rounded-lg p-4 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getCharacterIcon(character.type)}
                    <div>
                      <h4 className="font-semibold text-amber-800">{character.name}</h4>
                      <p className="text-sm text-amber-600 capitalize">{character.type}</p>
                      {character.description && (
                        <p className="text-sm text-amber-700 mt-1">{character.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingCharacter(character)}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Edit character"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteCharacter(character.characterId)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Delete character"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Character Modal */}
      {editingCharacter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-amber-800">Edit Character</h3>
              <button
                onClick={() => setEditingCharacter(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateCharacter(editingCharacter.characterId, {
                name: editingCharacter.name,
                type: editingCharacter.type,
                age: editingCharacter.age,
                gender: editingCharacter.gender,
                description: editingCharacter.description
              });
            }} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={editingCharacter.name}
                  onChange={(e) => setEditingCharacter(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Character name"
                  className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                  required
                />
                <select
                  value={editingCharacter.type}
                  onChange={(e) => setEditingCharacter(prev => ({ ...prev, type: e.target.value }))}
                  className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                >
                  <option value="human">Human</option>
                  <option value="animal">Animal</option>
                  <option value="object">Object</option>
                </select>
                <input
                  type="text"
                  value={editingCharacter.age || ''}
                  onChange={(e) => setEditingCharacter(prev => ({ ...prev, age: e.target.value }))}
                  placeholder="Age (optional)"
                  className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                />
                <select
                  value={editingCharacter.gender || ''}
                  onChange={(e) => setEditingCharacter(prev => ({ ...prev, gender: e.target.value }))}
                  className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                >
                  <option value="">Gender (optional)</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                </select>
              </div>
              <textarea
                value={editingCharacter.description || ''}
                onChange={(e) => setEditingCharacter(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Character description"
                className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                rows="3"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-amber-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:shadow-md transition-all duration-300 disabled:opacity-50 flex items-center gap-2"
                >
                  <FaSave />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingCharacter(null)}
                  className="px-4 py-2 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50 transition-all duration-300 flex items-center gap-2"
                >
                  <FaTimes />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterManager; 